import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Header } from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import {
  ClipboardCheck,
  Plus,
  FileText,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  Calendar,
  Target,
  BarChart3,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const auditTypes = [
  { id: 'medication', name: 'Medication Audit', description: 'MAR charts, storage, administration' },
  { id: 'care_planning', name: 'Care Planning Audit', description: 'Care plans, reviews, person-centred approach' },
  { id: 'documentation', name: 'Documentation Audit', description: 'Daily records, incident reports, completeness' },
  { id: 'infection_control', name: 'Infection Control Audit', description: 'Hand hygiene, PPE, cleanliness' },
  { id: 'health_safety', name: 'Health & Safety Audit', description: 'Risk assessments, fire safety, equipment' },
  { id: 'safeguarding', name: 'Safeguarding Audit', description: 'Training, referrals, policies' },
  { id: 'staff_files', name: 'Staff Files Audit', description: 'DBS, references, training records' },
  { id: 'environment', name: 'Environment Audit', description: 'Premises, equipment, accessibility' },
  { id: 'complaints', name: 'Complaints & Feedback Audit', description: 'Response times, outcomes, learning' },
  { id: 'custom', name: 'Custom Audit', description: 'Organization-specific audit' },
];

export default function QualityAssurance() {
  const { user, organization } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [reports, setReports] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);

  const [auditData, setAuditData] = useState({
    audit_type: '',
    area: '',
    audit_date: new Date().toISOString().split('T')[0],
    findings: [],
    recommendations: '',
    compliance_score: 80,
    evidence_files: [],
  });

  const [newFinding, setNewFinding] = useState({
    area: '',
    finding: '',
    severity: 'medium',
    action_required: '',
  });

  useEffect(() => {
    fetchData();
  }, [organization]);

  const fetchData = async () => {
    try {
      const [auditsRes] = await Promise.all([
        axios.get(`${API_URL}/api/qa/audits`, { params: { organization_id: organization?.id } }),
      ]);
      setAudits(auditsRes.data);
    } catch (error) {
      console.error('Error fetching QA data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddFinding = () => {
    if (!newFinding.area || !newFinding.finding) {
      toast.error('Please fill in finding details');
      return;
    }
    setAuditData(prev => ({
      ...prev,
      findings: [...prev.findings, { ...newFinding, id: Date.now() }]
    }));
    setNewFinding({ area: '', finding: '', severity: 'medium', action_required: '' });
  };

  const handleRemoveFinding = (id) => {
    setAuditData(prev => ({
      ...prev,
      findings: prev.findings.filter(f => f.id !== id)
    }));
  };

  const handleCreateAudit = async (e) => {
    e.preventDefault();
    if (!auditData.audit_type || !auditData.area) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      await axios.post(`${API_URL}/api/qa/audits`, {
        organization_id: organization?.id,
        audit_type: auditData.audit_type,
        area: auditData.area,
        auditor_id: user?.id,
        audit_date: auditData.audit_date,
        findings: auditData.findings,
        recommendations: auditData.recommendations.split('\n').filter(r => r.trim()),
        compliance_score: auditData.compliance_score,
        evidence_files: auditData.evidence_files,
      });

      toast.success('Audit created successfully');
      setIsCreateOpen(false);
      setAuditData({
        audit_type: '',
        area: '',
        audit_date: new Date().toISOString().split('T')[0],
        findings: [],
        recommendations: '',
        compliance_score: 80,
        evidence_files: [],
      });
      fetchData();
    } catch (error) {
      console.error('Error creating audit:', error);
      toast.error('Failed to create audit');
    }
  };

  const generateReport = async (reportType) => {
    setLoadingReport(true);
    try {
      const res = await axios.get(`${API_URL}/api/reports/${reportType}`, {
        params: { organization_id: organization?.id }
      });
      setReports(res.data);
      toast.success('Report generated successfully');
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report');
    } finally {
      setLoadingReport(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreBadge = (score) => {
    if (score >= 90) return 'bg-green-100 text-green-700 border-green-200';
    if (score >= 70) return 'bg-amber-100 text-amber-700 border-amber-200';
    return 'bg-red-100 text-red-700 border-red-200';
  };

  const averageScore = audits.length > 0
    ? Math.round(audits.reduce((acc, a) => acc + (a.compliance_score || 0), 0) / audits.length)
    : 0;

  return (
    <div className="min-h-screen" data-testid="qa-page">
      <Header title="Quality Assurance" />

      <div className="p-6 space-y-6">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Audits</p>
                  <p className="text-3xl font-bold">{audits.length}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <ClipboardCheck className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Average Score</p>
                  <p className={`text-3xl font-bold ${getScoreColor(averageScore)}`}>{averageScore}%</p>
                </div>
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-teal-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Issues Found</p>
                  <p className="text-3xl font-bold text-amber-600">
                    {audits.reduce((acc, a) => acc + (a.findings?.length || 0), 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Audit Types</p>
                  <p className="text-3xl font-bold">{new Set(audits.map(a => a.audit_type)).size}</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Target className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 max-w-xl">
            <TabsTrigger value="overview">Audits</TabsTrigger>
            <TabsTrigger value="templates">Audit Templates</TabsTrigger>
            <TabsTrigger value="reports">Reports</TabsTrigger>
          </TabsList>

          {/* Audits Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Quality Assurance Audits</h2>
              <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-teal-600 hover:bg-teal-700" data-testid="new-audit-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    New Audit
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Create Quality Audit</DialogTitle>
                    <DialogDescription>Conduct a new quality assurance audit</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateAudit} className="space-y-6 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Audit Type *</Label>
                        <Select value={auditData.audit_type} onValueChange={(v) => setAuditData(prev => ({ ...prev, audit_type: v }))}>
                          <SelectTrigger data-testid="audit-type-select">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            {auditTypes.map(t => (
                              <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Audit Date *</Label>
                        <Input
                          type="date"
                          value={auditData.audit_date}
                          onChange={(e) => setAuditData(prev => ({ ...prev, audit_date: e.target.value }))}
                          data-testid="audit-date"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Area/Location Being Audited *</Label>
                      <Input
                        value={auditData.area}
                        onChange={(e) => setAuditData(prev => ({ ...prev, area: e.target.value }))}
                        placeholder="e.g., Main Office, Client Records, Kitchen Area"
                        data-testid="audit-area"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Compliance Score: {auditData.compliance_score}%</Label>
                      <Input
                        type="range"
                        min="0"
                        max="100"
                        value={auditData.compliance_score}
                        onChange={(e) => setAuditData(prev => ({ ...prev, compliance_score: parseInt(e.target.value) }))}
                        className="w-full"
                        data-testid="audit-score"
                      />
                      <Progress value={auditData.compliance_score} className="h-2" />
                    </div>

                    {/* Findings Section */}
                    <div className="space-y-4">
                      <Label>Findings</Label>
                      <Card className="bg-slate-50">
                        <CardContent className="pt-4 space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <Input
                              placeholder="Area"
                              value={newFinding.area}
                              onChange={(e) => setNewFinding(prev => ({ ...prev, area: e.target.value }))}
                            />
                            <Select value={newFinding.severity} onValueChange={(v) => setNewFinding(prev => ({ ...prev, severity: v }))}>
                              <SelectTrigger>
                                <SelectValue placeholder="Severity" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="low">Low</SelectItem>
                                <SelectItem value="medium">Medium</SelectItem>
                                <SelectItem value="high">High</SelectItem>
                                <SelectItem value="critical">Critical</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <Input
                            placeholder="Finding description"
                            value={newFinding.finding}
                            onChange={(e) => setNewFinding(prev => ({ ...prev, finding: e.target.value }))}
                          />
                          <Input
                            placeholder="Action required"
                            value={newFinding.action_required}
                            onChange={(e) => setNewFinding(prev => ({ ...prev, action_required: e.target.value }))}
                          />
                          <Button type="button" variant="outline" size="sm" onClick={handleAddFinding}>
                            <Plus className="w-4 h-4 mr-1" /> Add Finding
                          </Button>
                        </CardContent>
                      </Card>

                      {auditData.findings.length > 0 && (
                        <div className="space-y-2">
                          {auditData.findings.map((finding) => (
                            <div key={finding.id} className="flex items-center justify-between p-3 bg-white border rounded-lg">
                              <div>
                                <p className="font-medium">{finding.area}</p>
                                <p className="text-sm text-slate-500">{finding.finding}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge className={
                                  finding.severity === 'critical' ? 'bg-red-100 text-red-700' :
                                  finding.severity === 'high' ? 'bg-orange-100 text-orange-700' :
                                  finding.severity === 'medium' ? 'bg-amber-100 text-amber-700' :
                                  'bg-green-100 text-green-700'
                                }>
                                  {finding.severity}
                                </Badge>
                                <Button type="button" variant="ghost" size="sm" onClick={() => handleRemoveFinding(finding.id)}>
                                  ×
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label>Recommendations (one per line)</Label>
                      <Textarea
                        value={auditData.recommendations}
                        onChange={(e) => setAuditData(prev => ({ ...prev, recommendations: e.target.value }))}
                        rows={4}
                        placeholder="Enter recommendations..."
                        data-testid="audit-recommendations"
                      />
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
                      <Button type="submit" className="bg-teal-600 hover:bg-teal-700" data-testid="submit-audit">
                        Create Audit
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Audits List */}
            <div className="space-y-4">
              {audits.length > 0 ? (
                audits.map((audit) => (
                  <Card key={audit.id} className="hover:shadow-md transition-shadow" data-testid={`audit-card-${audit.id}`}>
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                            <ClipboardCheck className="w-6 h-6 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold capitalize">{audit.audit_type?.replace('_', ' ')} Audit</h3>
                            <p className="text-sm text-slate-500">{audit.area}</p>
                            <div className="flex items-center gap-3 mt-2 text-sm text-slate-500">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {audit.audit_date}
                              </span>
                              <span>•</span>
                              <span>{audit.findings?.length || 0} findings</span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge variant="outline" className={getScoreBadge(audit.compliance_score)}>
                            {audit.compliance_score}% Compliant
                          </Badge>
                          <Progress value={audit.compliance_score} className="h-1 w-24 mt-2" />
                        </div>
                      </div>

                      {audit.recommendations && (
                        <div className="mt-4 pt-4 border-t">
                          <p className="text-sm font-medium mb-2">Key Recommendations:</p>
                          <div className="text-sm text-slate-600">
                            {typeof audit.recommendations === 'string' ? (
                              <p>{audit.recommendations}</p>
                            ) : (
                              <ul className="space-y-1">
                                {audit.recommendations.slice(0, 3).map((rec, i) => (
                                  <li key={i} className="flex items-start gap-2">
                                    <CheckCircle2 className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                                    {rec}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Card>
                  <CardContent className="py-12 text-center">
                    <ClipboardCheck className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">No audits conducted yet</p>
                    <p className="text-sm text-slate-400 mt-1">Click &quot;New Audit&quot; to create your first quality audit</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Audit Templates</CardTitle>
                <CardDescription>Pre-defined audit types aligned with CQC requirements</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {auditTypes.map((type) => (
                    <Card key={type.id} className="border-2 border-slate-100 hover:border-teal-200 transition-colors cursor-pointer"
                      onClick={() => {
                        setAuditData(prev => ({ ...prev, audit_type: type.id }));
                        setIsCreateOpen(true);
                      }}
                    >
                      <CardContent className="pt-6">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                            <ClipboardCheck className="w-5 h-5 text-teal-600" />
                          </div>
                          <div>
                            <p className="font-semibold">{type.name}</p>
                          </div>
                        </div>
                        <p className="text-sm text-slate-500">{type.description}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Generate Reports</CardTitle>
                <CardDescription>Generate comprehensive reports for compliance and management</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <Card className="border-2 border-slate-100 hover:border-teal-200 transition-colors cursor-pointer"
                    onClick={() => generateReport('compliance-overview')}
                  >
                    <CardContent className="pt-6 text-center">
                      <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mx-auto mb-3">
                        <BarChart3 className="w-6 h-6 text-blue-600" />
                      </div>
                      <p className="font-semibold">Compliance Overview</p>
                      <p className="text-sm text-slate-500 mt-1">CQC compliance scores and trends</p>
                    </CardContent>
                  </Card>

                  <Card className="border-2 border-slate-100 hover:border-teal-200 transition-colors cursor-pointer"
                    onClick={() => generateReport('staff-training')}
                  >
                    <CardContent className="pt-6 text-center">
                      <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center mx-auto mb-3">
                        <FileText className="w-6 h-6 text-green-600" />
                      </div>
                      <p className="font-semibold">Staff Training Report</p>
                      <p className="text-sm text-slate-500 mt-1">Training completion status</p>
                    </CardContent>
                  </Card>

                  <Card className="border-2 border-slate-100 hover:border-teal-200 transition-colors cursor-pointer"
                    onClick={() => generateReport('incident-summary')}
                  >
                    <CardContent className="pt-6 text-center">
                      <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center mx-auto mb-3">
                        <AlertCircle className="w-6 h-6 text-amber-600" />
                      </div>
                      <p className="font-semibold">Incident Summary</p>
                      <p className="text-sm text-slate-500 mt-1">Incidents by type and severity</p>
                    </CardContent>
                  </Card>

                  <Card className="border-2 border-slate-100 hover:border-teal-200 transition-colors cursor-pointer"
                    onClick={() => generateReport('hr-compliance')}
                  >
                    <CardContent className="pt-6 text-center">
                      <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mx-auto mb-3">
                        <Target className="w-6 h-6 text-purple-600" />
                      </div>
                      <p className="font-semibold">HR Compliance</p>
                      <p className="text-sm text-slate-500 mt-1">DBS, RTW, supervision status</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Report Display */}
                {loadingReport && (
                  <div className="mt-6 text-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto text-teal-600" />
                    <p className="text-slate-500 mt-2">Generating report...</p>
                  </div>
                )}

                {reports && !loadingReport && (
                  <Card className="mt-6 bg-slate-50">
                    <CardHeader>
                      <CardTitle className="capitalize">{reports.report_type?.replace('-', ' ')} Report</CardTitle>
                      <CardDescription>Generated at {new Date(reports.generated_at).toLocaleString()}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <pre className="text-sm bg-white p-4 rounded-lg overflow-auto max-h-96">
                        {JSON.stringify(reports.summary || reports.data, null, 2)}
                      </pre>
                    </CardContent>
                  </Card>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
